// The Calendar's pure parts (CALENDAR-C1, design §3): the four views' windows in the person's
// browser zone, the occurrences laid out per day (all-day row, time grid columns, month chips),
// the calendars' colours (the tag palette's tokens, never a literal), the repeat presets, the
// free gaps of "Find a time", and the remembered toggles (this device only, until a pref key).
import type { TagChipColor } from "@companyos/ui";
import type { CalendarOccurrence, CalendarView } from "../../api/types";

export const CALENDAR_VIEWS = ["day", "week", "month", "agenda"] as const;
export type CalendarViewKind = (typeof CALENDAR_VIEWS)[number];

/** Days the agenda lists from the day in view. */
export const AGENDA_DAYS = 14;

// Days ------------------------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, "0");

/** A local date as YYYY-MM-DD (the browser's zone). */
export function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight of a YYYY-MM-DD. */
export function parseDay(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function addDays(day: string, n: number): string {
  const d = parseDay(day);
  return ymd(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
}

function addMonths(day: string, n: number): string {
  const d = parseDay(day);
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return ymd(new Date(target.getFullYear(), target.getMonth(), Math.min(d.getDate(), last)));
}

/** The first day of the week holding `day`; `weekStart` 1 = Monday … 7 = Sunday (ISO). */
export function startOfWeek(day: string, weekStart = 1): string {
  const js = parseDay(day).getDay(); // 0 = Sunday
  const iso = js === 0 ? 7 : js;
  return addDays(day, -((iso - weekStart + 7) % 7));
}

/** The days a view shows around `anchor`, first to last. */
export function daysOf(view: CalendarViewKind, anchor: string, weekStart = 1): string[] {
  let first: string;
  let count: number;
  if (view === "day") {
    first = anchor;
    count = 1;
  } else if (view === "week") {
    first = startOfWeek(anchor, weekStart);
    count = 7;
  } else if (view === "agenda") {
    first = anchor;
    count = AGENDA_DAYS;
  } else {
    const d = parseDay(anchor);
    const monthFirst = ymd(new Date(d.getFullYear(), d.getMonth(), 1));
    const monthLast = ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    first = startOfWeek(monthFirst, weekStart);
    const last = addDays(startOfWeek(monthLast, weekStart), 6);
    count = Math.round((parseDay(last).getTime() - parseDay(first).getTime()) / 86_400_000) + 1;
  }
  return Array.from({ length: count }, (_, i) => addDays(first, i));
}

/** The window the API is asked for: local midnight of the first day to that of the day after. */
export function windowOf(days: readonly string[]): { from: string; to: string } {
  const first = days[0] ?? ymd(new Date());
  const last = days.at(-1) ?? first;
  return {
    from: parseDay(first).toISOString(),
    to: parseDay(addDays(last, 1)).toISOString(),
  };
}

/** Previous / next for a view. */
export function stepAnchor(view: CalendarViewKind, anchor: string, n: number): string {
  if (view === "day") return addDays(anchor, n);
  if (view === "week") return addDays(anchor, 7 * n);
  if (view === "agenda") return addDays(anchor, AGENDA_DAYS * n);
  return addMonths(anchor, n);
}

const fmt = (opts: Intl.DateTimeFormatOptions, d: Date) =>
  new Intl.DateTimeFormat("en-GB", opts).format(d);

/** The toolbar's title. */
export function viewTitle(view: CalendarViewKind, days: readonly string[], anchor: string): string {
  const a = parseDay(anchor);
  if (view === "month") return fmt({ month: "long", year: "numeric" }, a);
  if (view === "day") return fmt({ weekday: "long", day: "numeric", month: "long" }, a);
  const first = parseDay(days[0] ?? anchor);
  const last = parseDay(days.at(-1) ?? anchor);
  const sameMonth = first.getMonth() === last.getMonth();
  return sameMonth
    ? `${first.getDate()} – ${fmt({ day: "numeric", month: "long", year: "numeric" }, last)}`
    : `${fmt({ day: "numeric", month: "short" }, first)} – ${fmt({ day: "numeric", month: "short", year: "numeric" }, last)}`;
}

export const dayLabel = (day: string, style: "short" | "long" = "short") =>
  style === "long"
    ? fmt({ weekday: "long", day: "numeric", month: "long" }, parseDay(day))
    : fmt({ weekday: "short", day: "numeric" }, parseDay(day));

/** "14:00" in the browser's zone. */
export function clock(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "14:00 – 15:00", or "All day". */
export function timeRange(o: Pick<CalendarOccurrence, "starts_at" | "ends_at" | "all_day">) {
  return o.all_day ? "All day" : `${clock(o.starts_at)} – ${clock(o.ends_at)}`;
}

// Occurrences per day ---------------------------------------------------------------------------

/** A date of an instant in a zone (all-day events carry their dates in their own zone). */
export function dateIn(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * The days of the view (`days`, first to last) an occurrence covers: its dates for an all-day
 * one, else the local days it touches, clipped to the view however long the event runs.
 */
export function daysCovered(o: CalendarOccurrence, days: readonly string[]): string[] {
  let first: string;
  let last: string;
  if (o.all_day) {
    first = dateIn(o.starts_at, o.timezone);
    const end = dateIn(o.ends_at, o.timezone);
    // The end is exclusive; the same day means one day.
    last = end > first ? addDays(end, -1) : first;
  } else {
    first = ymd(new Date(o.starts_at));
    // An event ending at midnight does not touch the next day.
    last = ymd(new Date(new Date(o.ends_at).getTime() - 1));
    if (last < first) last = first;
  }
  return days.filter((d) => d >= first && d <= last);
}

export type DayBuckets = { allDay: CalendarOccurrence[]; timed: CalendarOccurrence[] };

/** Occurrences by local day of the view, all-day apart, each list by start. */
export function byDay(
  list: readonly CalendarOccurrence[],
  days: readonly string[],
): Map<string, DayBuckets> {
  const out = new Map<string, DayBuckets>();
  const sorted = [...list].sort(
    (a, b) => a.starts_at.localeCompare(b.starts_at) || a.key.localeCompare(b.key),
  );
  for (const o of sorted) {
    for (const day of daysCovered(o, days)) {
      let bucket = out.get(day);
      if (!bucket) {
        bucket = { allDay: [], timed: [] };
        out.set(day, bucket);
      }
      (o.all_day ? bucket.allDay : bucket.timed).push(o);
    }
  }
  return out;
}

export type Placed = {
  o: CalendarOccurrence;
  /** Minutes from the day's local midnight. */
  top: number;
  height: number;
  /** The overlap lane and how many lanes its cluster has. */
  lane: number;
  lanes: number;
};

/** Minutes after midnight on an instant's local wall clock (the grid's axis). */
export const wallMinutes = (d: Date) => d.getHours() * 60 + d.getMinutes();

/**
 * Timed occurrences of one day placed in lanes (overlaps side by side). Positions come from the
 * local wall clock, not the time elapsed since midnight, so 09:00 sits at 09:00 on a DST day.
 */
export function layoutDay(day: string, timed: readonly CalendarOccurrence[]): Placed[] {
  const items = timed.map((o) => {
    const s = new Date(o.starts_at);
    const e = new Date(o.ends_at);
    const start = ymd(s) < day ? 0 : wallMinutes(s);
    let end = ymd(e) > day ? 1440 : wallMinutes(e);
    // The repeated hour of a fall-back night: the wall clock goes back, the length does not.
    if (end <= start && e > s) end = Math.min(1440, start + (e.getTime() - s.getTime()) / 60_000);
    return { o, start, end: Math.max(end, start + 15) };
  });
  items.sort((a, b) => a.start - b.start || b.end - a.end);
  const placed: Placed[] = [];
  let cluster: { item: (typeof items)[number]; lane: number }[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = Math.max(1, ...cluster.map((c) => c.lane + 1));
    for (const c of cluster)
      placed.push({
        o: c.item.o,
        top: c.item.start,
        height: c.item.end - c.item.start,
        lane: c.lane,
        lanes,
      });
    cluster = [];
  };
  for (const item of items) {
    if (item.start >= clusterEnd && cluster.length) flush();
    const taken = new Set(cluster.filter((c) => c.item.end > item.start).map((c) => c.lane));
    let lane = 0;
    while (taken.has(lane)) lane++;
    cluster.push({ item, lane });
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  if (cluster.length) flush();
  return placed;
}

// The company's clock in the time grid -----------------------------------------------------------

/**
 * The company's working hours on its own clock. No company setting holds them yet, so the
 * grid folds around 08:00–18:00 (design §4) until one does.
 */
export const WORK_HOURS = { from: 8, to: 18 } as const;

/**
 * The viewer's hours [from, to) the company's working hours cover on `day` (whole hours, widened
 * to fit); null when they cross the viewer's midnight (nothing is folded then).
 */
export function workWindow(
  day: string,
  companyZone: string,
  hours: { from: number; to: number } = WORK_HOURS,
): { from: number; to: number } | null {
  const s = fromZonedInput(`${day}T${pad(hours.from)}:00`, companyZone);
  const e = fromZonedInput(`${day}T${pad(hours.to)}:00`, companyZone);
  if (!s || !e) return null;
  const sd = new Date(s);
  const ed = new Date(e);
  if (ymd(sd) !== day) return null;
  const endsAtMidnight = ymd(ed) === addDays(day, 1) && wallMinutes(ed) === 0;
  if (ymd(ed) !== day && !endsAtMidnight) return null;
  const from = Math.floor(wallMinutes(sd) / 60);
  const to = endsAtMidnight ? 24 : Math.ceil(wallMinutes(ed) / 60);
  return to > from ? { from, to } : null;
}

/** Does a zone's clock differ from the viewer's on `day`? */
export function zoneDiffers(zone: string, day: string): boolean {
  const noon = atWall(day, 12 * 60);
  return zonedInput(noon, zone) !== localInput(noon);
}

/** The company's "HH:mm" at the viewer's hour `hour` of `day` (the grid's second axis). */
export function companyClock(day: string, hour: number, zone: string): string {
  return zonedInput(atWall(day, hour * 60), zone).slice(11);
}

/** A zone's short name on `day` ("EDT", "GMT+1"), for the axis heads. */
export function zoneShort(zone: string, day: string): string {
  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
        .formatToParts(atWall(day, 12 * 60))
        .find((p) => p.type === "timeZoneName")?.value ?? zone
    );
  } catch {
    return zone;
  }
}

/**
 * The hours a folded grid shows: the working window widened to whole hours covering every timed
 * block in view (`top`/`height` in minutes), so the fold only ever hides empty hours.
 */
export function shownHours(
  work: { from: number; to: number },
  placed: readonly { top: number; height: number }[],
): { from: number; to: number } {
  let from = work.from;
  let to = work.to;
  for (const p of placed) {
    from = Math.min(from, Math.floor(p.top / 60));
    to = Math.max(to, Math.ceil((p.top + p.height) / 60));
  }
  return { from: Math.max(0, from), to: Math.min(24, to) };
}

/**
 * A placed block clipped to the shown hours [from, to): its top and height from the shown top,
 * or null when it lies wholly in a folded band.
 */
export function clipToHours(
  p: { top: number; height: number },
  from: number,
  to: number,
): { top: number; height: number } | null {
  const lo = from * 60;
  const hi = to * 60;
  const top = Math.max(p.top, lo);
  const bottom = Math.min(p.top + p.height, hi);
  if (bottom <= top) return null;
  return { top: top - lo, height: bottom - top };
}

// Colours ---------------------------------------------------------------------------------------

export const CALENDAR_COLORS: readonly TagChipColor[] = [
  "sky",
  "violet",
  "emerald",
  "amber",
  "rose",
  "indigo",
  "teal",
  "orange",
];

/** A calendar's colour: its (or the follow's) palette key, else one picked from its id. */
export function colorOf(
  calendarId: string,
  calendars: ReadonlyMap<string, Pick<CalendarView, "display_color" | "kind">>,
): TagChipColor {
  const cal = calendars.get(calendarId);
  const named = cal?.display_color;
  if (named && (CALENDAR_COLORS as readonly string[]).includes(named)) return named as TagChipColor;
  if (cal?.kind === "company") return "sky";
  let h = 0;
  for (const ch of calendarId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CALENDAR_COLORS[h % CALENDAR_COLORS.length] ?? "sky";
}

// Repeat ----------------------------------------------------------------------------------------

export type RepeatKind = "none" | "daily" | "weekly" | "monthly" | "custom";
const BYDAY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

/** The RRULE of a repeat preset for an event starting on `day`; null for none. */
export function repeatRule(kind: RepeatKind, day: string, custom: string): string | null {
  if (kind === "daily") return "FREQ=DAILY";
  if (kind === "weekly") return `FREQ=WEEKLY;BYDAY=${BYDAY[parseDay(day).getDay()]}`;
  if (kind === "monthly") return `FREQ=MONTHLY;BYMONTHDAY=${parseDay(day).getDate()}`;
  if (kind === "custom") return custom.trim().replace(/^RRULE:/i, "") || null;
  return null;
}

/** The preset a stored rule reads as (anything else is custom). */
export function repeatKindOf(rule: string | null | undefined): RepeatKind {
  if (!rule) return "none";
  if (rule === "FREQ=DAILY") return "daily";
  if (/^FREQ=WEEKLY;BYDAY=[A-Z]{2}$/.test(rule)) return "weekly";
  if (/^FREQ=MONTHLY;BYMONTHDAY=\d{1,2}$/.test(rule)) return "monthly";
  return "custom";
}

// Find a time -----------------------------------------------------------------------------------

export type Gap = { start: Date; end: Date };

/**
 * Free gaps on `day` between `fromHour` and `toHour` (local) of at least `minMinutes`, around
 * every shown occurrence (busy blocks included; declined and cancelled ones do not block). An
 * all-day event on the day (an absence, an offsite) fills it.
 */
export function freeGaps(
  day: string,
  list: readonly CalendarOccurrence[],
  opts: { fromHour?: number; toHour?: number; minMinutes?: number } = {},
): Gap[] {
  const { fromHour = 8, toHour = 18, minMinutes = 30 } = opts;
  const d = parseDay(day);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), fromHour);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), toHour);
  const blocking = list.filter((o) => o.my_response !== "declined" && o.status !== "cancelled");
  if (blocking.some((o) => o.all_day && daysCovered(o, [day]).length > 0)) return [];
  const blocks = blocking
    .filter((o) => !o.all_day)
    .map((o) => ({ s: new Date(o.starts_at), e: new Date(o.ends_at) }))
    .filter((b) => b.e > start && b.s < end)
    .sort((a, b) => a.s.getTime() - b.s.getTime());
  const gaps: Gap[] = [];
  let cursor = start;
  for (const b of blocks) {
    if (b.s > cursor) gaps.push({ start: cursor, end: b.s });
    if (b.e > cursor) cursor = b.e;
  }
  if (cursor < end) gaps.push({ start: cursor, end });
  return gaps.filter((g) => g.end.getTime() - g.start.getTime() >= minMinutes * 60_000);
}

/** A new event's default length, in minutes. */
export const DEFAULT_EVENT_MINUTES = 60;

/** The length an event made from a free gap takes: the default, cut to end inside the gap. */
export function pickLength(g: Gap, length = DEFAULT_EVENT_MINUTES): number {
  return Math.min(length, Math.floor((g.end.getTime() - g.start.getTime()) / 60_000));
}

// Remembered toggles (this device; a `me.prefs` key may replace them) ----------------------------

export const TASKS_TOGGLE_KEY = "companyos.calendar.tasks";
export const BOARD_EVENTS_KEY = "companyos.board.events";

export function readToggle(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function writeToggle(key: string, on: boolean): void {
  try {
    window.localStorage.setItem(key, on ? "1" : "0");
  } catch {
    // Storage blocked: the toggle starts off next time.
  }
}

// Times in forms --------------------------------------------------------------------------------

/** "YYYY-MM-DDTHH:mm" for a datetime-local input, in the browser's zone. */
export function localInput(d: Date): string {
  return `${ymd(d)}T${clock(d)}`;
}

/** A datetime-local value (local) as an ISO instant; null when it does not parse. */
export function fromLocalInput(v: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(v);
  if (!m) return null;
  const [y, mo, day, h, min] = m.slice(1).map(Number) as [number, number, number, number, number];
  const d = new Date(y, mo - 1, day, h, min);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** The browser's zone (the API's `timezone` for a new all-day event's dates). */
export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** How far a zone's wall clock is ahead of UTC at an instant, in ms. */
function zoneOffset(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(at));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const wall = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return wall - Math.floor(at / 1000) * 1000;
}

/** "YYYY-MM-DDTHH:mm" of an instant on a zone's wall clock (a datetime-local in that zone). */
export function zonedInput(at: string | Date, timeZone: string): string {
  const d = typeof at === "string" ? new Date(at) : at;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
  } catch {
    return localInput(d);
  }
}

/** A datetime-local value read on a zone's wall clock, as an ISO instant; null if bad. */
export function fromZonedInput(v: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(v);
  if (!m) return null;
  const [y, mo, day, h, min] = m.slice(1).map(Number) as [number, number, number, number, number];
  const guess = Date.UTC(y, mo - 1, day, h, min);
  if (Number.isNaN(guess)) return null;
  try {
    let t = guess - zoneOffset(guess, timeZone);
    t = guess - zoneOffset(t, timeZone);
    return new Date(t).toISOString();
  } catch {
    return fromLocalInput(v);
  }
}

/** Midnight of a YYYY-MM-DD in a zone, as an ISO instant (an all-day event's bound). */
export function zonedMidnight(day: string, timeZone: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const guess = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  try {
    let t = guess - zoneOffset(guess, timeZone);
    t = guess - zoneOffset(t, timeZone);
    return new Date(t).toISOString();
  } catch {
    return parseDay(day).toISOString();
  }
}

// Attendees in forms ----------------------------------------------------------------------------

type AttendeeRecord = { membership_id: string | null; email: string | null; optional: boolean };
export type AttendeeInput =
  | { membership_id: string; optional?: boolean }
  | { email: string; optional?: boolean };

/**
 * The attendee list a form sends: the records still picked keep their `optional` flag (outside
 * emails included), then the members newly picked.
 */
export function attendeeInput(
  records: readonly AttendeeRecord[],
  members: readonly string[],
  emails: readonly string[],
): AttendeeInput[] {
  const out: AttendeeInput[] = [];
  for (const a of records) {
    if (a.membership_id) {
      if (members.includes(a.membership_id))
        out.push({ membership_id: a.membership_id, optional: a.optional });
    } else if (a.email && emails.includes(a.email))
      out.push({ email: a.email, optional: a.optional });
  }
  for (const m of members)
    if (!records.some((r) => r.membership_id === m)) out.push({ membership_id: m });
  return out;
}

export const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

// Move and resize -------------------------------------------------------------------------------

export type EditScope = "this" | "following" | "all";

/** Drags snap to a quarter hour. */
export const SNAP_MINUTES = 15;
export const snapMinutes = (m: number) => Math.round(m / SNAP_MINUTES) * SNAP_MINUTES;

const dayDiff = (a: string, b: string) =>
  Math.round((parseDay(a).getTime() - parseDay(b).getTime()) / 86_400_000);

const atWall = (day: string, minute: number) => {
  const d = parseDay(day);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, minute);
};

export type NewTimes = { starts_at: string; ends_at: string };

/**
 * An occurrence dropped on `day`: a timed one at `minute` on the wall clock (null keeps its time
 * of day, as the month grid does), its length kept; an all-day one by whole days in its own zone.
 * Null when nothing moves.
 */
export function movedTimes(
  o: Pick<CalendarOccurrence, "starts_at" | "ends_at" | "all_day" | "timezone">,
  day: string,
  minute: number | null,
): NewTimes | null {
  if (o.all_day) {
    const from = dateIn(o.starts_at, o.timezone);
    const n = dayDiff(day, from);
    if (n === 0) return null;
    return {
      starts_at: zonedMidnight(addDays(from, n), o.timezone),
      ends_at: zonedMidnight(addDays(dateIn(o.ends_at, o.timezone), n), o.timezone),
    };
  }
  const s = new Date(o.starts_at);
  const length = Date.parse(o.ends_at) - s.getTime();
  const m = Math.min(1440 - SNAP_MINUTES, Math.max(0, minute ?? wallMinutes(s)));
  const next = atWall(day, m);
  if (next.getTime() === s.getTime()) return null;
  return {
    starts_at: next.toISOString(),
    ends_at: new Date(next.getTime() + length).toISOString(),
  };
}

/** A timed occurrence's end dragged to `minute` of `day` (at least a quarter hour long). */
export function resizedTimes(
  o: Pick<CalendarOccurrence, "starts_at" | "ends_at">,
  day: string,
  minute: number,
): NewTimes | null {
  const s = Date.parse(o.starts_at);
  const end = Math.max(atWall(day, Math.min(1440, minute)).getTime(), s + SNAP_MINUTES * 60_000);
  if (end === Date.parse(o.ends_at)) return null;
  return { starts_at: new Date(s).toISOString(), ends_at: new Date(end).toISOString() };
}

/** The versions a detail read carries: the occurrence's and, on a series, the master's. */
export type VersionsRead = { version: number; series_version: number | null };

/**
 * The version every write (edit, move, resize, delete) sends for its conflict check: the
 * series' for "following" and "all" (they change the master), else the occurrence's or the
 * single event's.
 */
export function versionFor(scope: EditScope | null, read: VersionsRead): number {
  return scope === "following" || scope === "all"
    ? (read.series_version ?? read.version)
    : read.version;
}

/** A drag's PATCH body: the new times, the scope and slot on a series, the version read. */
export function moveBody(
  o: Pick<CalendarOccurrence, "original_start_at" | "starts_at">,
  times: NewTimes,
  scope: EditScope | null,
  read: VersionsRead,
) {
  return {
    ...times,
    ...(scope ? { scope, occurrence_start: o.original_start_at ?? o.starts_at } : {}),
    version: versionFor(scope, read),
  };
}
